// Hook для работы с черновиками в localStorage
import { useEffect, useRef, useCallback } from 'react';
import type { Row, Draft } from './types';

const DRAFT_PREFIX = 'editable_table_draft_';
const AUTOSAVE_INTERVAL = 5000; // 5 секунд

interface UseDraftOptions {
  draftKey: string;
  changes: Map<string, Partial<Row>>;
  onRestore: (changes: Map<string, Partial<Row>>) => void;
}

export function useDraft({ draftKey, changes, onRestore }: UseDraftOptions) {
  const autosaveTimerRef = useRef<number | null>(null);
  const lastSaveTimeRef = useRef<number>(0);

  // Ключ для localStorage
  const storageKey = `${DRAFT_PREFIX}${draftKey}`;

  /**
   * Сохранить черновик в localStorage
   */
  const saveDraft = useCallback(() => {
    if (changes.size === 0) {
      // Если нет изменений, удаляем черновик
      localStorage.removeItem(storageKey);
      lastSaveTimeRef.current = 0;
      return;
    }

    const draft: Draft = {
      timestamp: Date.now(),
      changes: changes,
    };

    try {
      // Map нельзя напрямую сериализовать в JSON, конвертируем в объект
      const serializable = {
        timestamp: draft.timestamp,
        changes: Object.fromEntries(draft.changes),
      };

      localStorage.setItem(storageKey, JSON.stringify(serializable));
      lastSaveTimeRef.current = draft.timestamp;
    } catch (error) {
      console.error('Ошибка сохранения черновика:', error);
    }
  }, [changes, storageKey]);

  /**
   * Загрузить черновик из localStorage
   */
  const loadDraft = useCallback((): Draft | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const parsed = JSON.parse(stored);

      // Конвертируем обратно в Map
      const draft: Draft = {
        timestamp: parsed.timestamp,
        changes: new Map(Object.entries(parsed.changes)),
      };

      return draft;
    } catch (error) {
      console.error('Ошибка загрузки черновика:', error);
      return null;
    }
  }, [storageKey]);

  /**
   * Удалить черновик
   */
  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    lastSaveTimeRef.current = 0;
  }, [storageKey]);

  /**
   * Восстановить черновик (с подтверждением)
   */
  const restoreDraft = useCallback(() => {
    const draft = loadDraft();
    if (!draft) return false;

    const confirmed = window.confirm(
      `Найден несохранённый черновик от ${new Date(draft.timestamp).toLocaleString('ru-RU')}. Восстановить?`
    );

    if (confirmed) {
      onRestore(draft.changes);
      return true;
    }

    return false;
  }, [loadDraft, onRestore]);

  /**
   * Получить время последнего сохранения
   */
  const getLastSaveTime = useCallback(() => {
    return lastSaveTimeRef.current;
  }, []);

  // Автосохранение каждые 5 секунд
  useEffect(() => {
    autosaveTimerRef.current = setInterval(() => {
      saveDraft();
    }, AUTOSAVE_INTERVAL);

    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }
    };
  }, [saveDraft]);

  // При монтировании компонента проверяем наличие черновика
  useEffect(() => {
    const draft = loadDraft();
    if (draft && draft.changes.size > 0) {
      // Показываем диалог восстановления только если есть изменения
      setTimeout(() => restoreDraft(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    saveDraft,
    loadDraft,
    clearDraft,
    restoreDraft,
    getLastSaveTime,
  };
}
